/**
 * csv.js
 * ------
 * Minimal client-side CSV parsing/export for the student roster CSV import —
 * no dependency needed, mirrors the hand-rolled CSV export already used in
 * admin/EventRegistrationSection.js.
 */

// RFC4180-ish: handles quoted fields (with embedded commas/quotes/newlines)
// and both \n and \r\n line endings.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const s = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text; // strip BOM (common from Excel/Sheets exports)

  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); } // last field/row

  return rows.filter(r => !(r.length === 1 && r[0].trim() === ''));
}

// Tolerant header match: case-insensitive, ignores spacing/punctuation, so
// small rewording ("First Name" vs "first_name") doesn't break the import.
function headerMatches(header, terms) {
  const normalized = header.trim().toLowerCase().replace(/[^a-z]/g, '');
  return terms.some(t => normalized === t);
}

const HEADER_SPEC = [
  { key: 'firstName', label: 'First Name', terms: ['firstname', 'first'] },
  { key: 'lastName', label: 'Last Name', terms: ['lastname', 'last'] },
  { key: 'email', label: 'Email', terms: ['email', 'emailaddress'] },
];

// Parses a students CSV into { firstName, lastName, email } rows. Returns
// { error } if a required column can't be found in the header row, otherwise
// { rows } — one entry per non-blank data row, in file order.
export function parseStudentCsv(text) {
  const table = parseCsv(text);
  if (table.length === 0) return { error: 'The file is empty.' };

  const [header, ...dataRows] = table;
  const colIndex = {};
  for (const { key, label, terms } of HEADER_SPEC) {
    const idx = header.findIndex((h) => headerMatches(h, terms));
    if (idx === -1) {
      return { error: `Couldn't find a "${label}" column. Use the template below so headers match.` };
    }
    colIndex[key] = idx;
  }

  const rows = dataRows
    .filter((r) => r.some((f) => f.trim() !== ''))
    .map((r) => ({
      firstName: (r[colIndex.firstName] || '').trim(),
      lastName: (r[colIndex.lastName] || '').trim(),
      email: (r[colIndex.email] || '').trim(),
    }));

  return { rows };
}

export function downloadCsvTemplate() {
  const csv = 'First Name,Last Name,Email\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'dancer-roster-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
